import { ColumnType, type SmartColumn } from "@/queries/smart-tables/types";
import { isExecutionMetadataMetricKey } from "@/routes/smart-table/utils/columnTypeGuards";
import {
  isSmartTableColumnGroupDef,
  type SmartTableColumnDef,
} from "@/routes/smart-table/utils/smartTableColumnGroups";

export interface GroupableColumn {
  id: string;
  title: string;
  type?: ColumnType;
  config?: Record<string, unknown> | null;
  group_parent_column_id?: SmartColumn["group_parent_column_id"];
  group_child_column_ids?: SmartColumn["group_child_column_ids"];
  metric_key?: SmartColumn["metric_key"];
  metric_label?: SmartColumn["metric_label"];
  system_kind?: SmartColumn["system_kind"];
}

type LeafColumnDef<TData> = Exclude<SmartTableColumnDef<TData>, { children: unknown[] }>;

const EXECUTION_METADATA_PARENT_TYPES = new Set<ColumnType | string>([
  ColumnType.PROMPT_TEMPLATE,
  ColumnType.WORKFLOW,
  ColumnType.TRACE,
  ColumnType.REQUEST,
]);

const ROOT_LEAF_HEADERS: Partial<Record<ColumnType, string>> = {
  [ColumnType.WORKFLOW]: "Workflow",
  [ColumnType.TRACE]: "Trace",
  [ColumnType.REQUEST]: "Request",
};

export const METRIC_GROUP_ID_SUFFIX = "__metric_group";

export const getMetricGroupId = (columnId: string): string =>
  `${columnId}${METRIC_GROUP_ID_SUFFIX}`;

export const resolveMetricGroupParentId = (groupId: string): string | null =>
  groupId.endsWith(METRIC_GROUP_ID_SUFFIX)
    ? groupId.slice(0, -METRIC_GROUP_ID_SUFFIX.length)
    : null;

export const mapMetricGroupColumnDefs = <TColumn extends { id: string }, TData>(
  columns: readonly TColumn[],
  columnDefs: readonly SmartTableColumnDef<TData>[],
  mapGroup: (
    columnDef: Extract<SmartTableColumnDef<TData>, { children: unknown[] }>,
    parentColumn: TColumn,
  ) => SmartTableColumnDef<TData>,
): SmartTableColumnDef<TData>[] => {
  const columnsById = new Map(columns.map(column => [column.id, column]));
  return columnDefs.map(columnDef => {
    if (!isSmartTableColumnGroupDef(columnDef)) {
      return columnDef;
    }
    const groupId = typeof columnDef.groupId === "string" ? columnDef.groupId : "";
    const parentId = resolveMetricGroupParentId(groupId);
    if (!parentId) {
      return columnDef;
    }
    const parentColumn = columnsById.get(parentId);
    if (!parentColumn) {
      return columnDef;
    }
    return mapGroup(columnDef, parentColumn);
  });
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const nonEmptyString = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

const objectContext = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};

const configParentId = (column: GroupableColumn, key: "source_column_id") =>
  isRecord(column.config) ? nonEmptyString(column.config[key]) : null;

export const supportsExecutionMetadataColumns = (
  column?: Pick<GroupableColumn, "type"> | null,
): boolean => (column?.type ? EXECUTION_METADATA_PARENT_TYPES.has(column.type) : false);

export const isExecutionMetadataChildColumn = (column?: GroupableColumn | null): boolean => {
  if (!column) {
    return false;
  }
  if (isRecord(column.config) && column.config.composition_kind === "execution_metadata") {
    return true;
  }
  return (
    isExecutionMetadataMetricKey(column.metric_key) &&
    Boolean(nonEmptyString(column.group_parent_column_id))
  );
};

export const resolveExecutionMetadataParentColumnId = (
  column?: GroupableColumn | null,
): string | null =>
  isExecutionMetadataChildColumn(column) && column
    ? (nonEmptyString(column.group_parent_column_id) ?? configParentId(column, "source_column_id"))
    : null;

export const resolveGroupedChildParentColumnId = (column?: GroupableColumn | null): string | null =>
  resolveExecutionMetadataParentColumnId(column);

export const isTopLevelGroupedChildColumn = (column?: GroupableColumn | null): boolean =>
  isExecutionMetadataChildColumn(column);

export const isNestedChildOfParent = (
  childColumn: GroupableColumn | null | undefined,
  parentColumn: GroupableColumn | null | undefined,
): boolean => {
  if (!childColumn || !parentColumn) {
    return false;
  }
  if (resolveGroupedChildParentColumnId(childColumn) !== parentColumn.id) {
    return false;
  }
  return (
    isExecutionMetadataChildColumn(childColumn) && supportsExecutionMetadataColumns(parentColumn)
  );
};

export const getExplicitGroupedChildColumnIds = (column?: GroupableColumn | null): string[] => {
  const raw = column?.group_child_column_ids;
  return Array.isArray(raw)
    ? raw.filter((childId): childId is string => Boolean(nonEmptyString(childId)))
    : [];
};

export const resolveGroupedChildIdsForParent = (
  parentColumn: GroupableColumn,
  columns: readonly GroupableColumn[],
): string[] =>
  Array.from(
    new Set([
      ...getExplicitGroupedChildColumnIds(parentColumn),
      ...columns.filter(child => isNestedChildOfParent(child, parentColumn)).map(child => child.id),
    ]),
  );

export const resolveMetricKey = (column?: GroupableColumn | null): string | null => {
  if (!column) {
    return null;
  }
  if (isExecutionMetadataMetricKey(column.metric_key)) {
    return column.metric_key;
  }
  return isRecord(column.config) && isExecutionMetadataMetricKey(column.config.metric_key)
    ? column.config.metric_key
    : null;
};

const toNormalCaseMetricHeader = (value: string): string => {
  const trimmed = value.trim();
  return trimmed.length === 0 ? value : `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
};

export const resolveExecutionMetadataChildHeader = (
  parentColumn: GroupableColumn,
  childColumn: GroupableColumn,
): string => {
  if (typeof childColumn.metric_label === "string" && childColumn.metric_label.trim()) {
    return toNormalCaseMetricHeader(childColumn.metric_label);
  }
  const parentTitle = parentColumn.title.trim();
  if (parentTitle && childColumn.title.startsWith(`${parentTitle}.`)) {
    return toNormalCaseMetricHeader(childColumn.title.slice(parentTitle.length + 1));
  }
  return toNormalCaseMetricHeader(childColumn.title);
};

export const resolveRootLeafHeader = (parentColumn: GroupableColumn): string => {
  if (parentColumn.type === ColumnType.PROMPT_TEMPLATE) {
    return isRecord(parentColumn.config) && parentColumn.config.mock_tool_responses === true
      ? "Trace"
      : "Prompt";
  }
  return (parentColumn.type && ROOT_LEAF_HEADERS[parentColumn.type]) || parentColumn.title;
};

const getLeafColumnId = <TData>(column: SmartTableColumnDef<TData>): string | null =>
  isSmartTableColumnGroupDef(column)
    ? null
    : (nonEmptyString(column.colId) ?? nonEmptyString(column.field));

const withPinGroupContext = <TData>(
  children: LeafColumnDef<TData>[],
  groupLabel: string,
): LeafColumnDef<TData>[] => {
  const pinGroupColIds = Array.from(
    new Set(children.map(getLeafColumnId).filter((id): id is string => Boolean(id))),
  );
  const pinGroupLabel = groupLabel.trim() || "group";
  return children.map(child => ({
    ...child,
    context: { ...objectContext(child.context), pinGroupColIds, pinGroupLabel },
  }));
};

export const nestSmartTableGroupedChildColumns = <TData>({
  columns,
  columnDefs,
}: {
  columns: readonly GroupableColumn[];
  columnDefs: readonly SmartTableColumnDef<TData>[];
}): SmartTableColumnDef<TData>[] => {
  const columnsById = new Map(columns.map(column => [column.id, column]));
  const topLevelLeafById = new Map(
    columnDefs.flatMap(columnDef => {
      const columnId = getLeafColumnId(columnDef);
      return columnId && !isSmartTableColumnGroupDef(columnDef)
        ? ([[columnId, columnDef]] as const)
        : [];
    }),
  );
  const consumedChildIds = new Set<string>();
  const nestedDefs: SmartTableColumnDef<TData>[] = [];

  for (const columnDef of columnDefs) {
    if (isSmartTableColumnGroupDef(columnDef)) {
      nestedDefs.push(columnDef);
      continue;
    }

    const columnId = getLeafColumnId(columnDef);
    const parentColumn = columnId ? columnsById.get(columnId) : undefined;
    if (!columnId || !parentColumn || isTopLevelGroupedChildColumn(parentColumn)) {
      continue;
    }

    const groupedChildren = resolveGroupedChildIdsForParent(parentColumn, columns).flatMap<
      LeafColumnDef<TData>
    >(childId => {
      const childColumn = columnsById.get(childId);
      const childDef = topLevelLeafById.get(childId);
      if (!childColumn || !childDef || !isNestedChildOfParent(childColumn, parentColumn)) {
        return [];
      }
      consumedChildIds.add(childId);
      const context = objectContext(childDef.context);
      const headerName = resolveExecutionMetadataChildHeader(parentColumn, childColumn);
      const metricKey = resolveMetricKey(childColumn);
      return [
        {
          ...childDef,
          headerName,
          editable: false,
          editableColumn: false,
          columnGroupShow: "open" as const,
          headerComponentParams: {
            ...objectContext(childDef.headerComponentParams),
            metricKey,
            title: headerName,
            configColumnId: parentColumn.id,
          },
          cellRendererParams: {
            ...objectContext(childDef.cellRendererParams),
            parentColumnId: parentColumn.id,
            metricKey,
          },
          context: {
            ...context,
            parentColumnId: parentColumn.id,
            configColumnId: parentColumn.id,
            ...(metricKey ? { metricKey } : {}),
          },
        },
      ];
    });

    if (groupedChildren.length === 0) {
      if (!consumedChildIds.has(columnId)) {
        nestedDefs.push(columnDef);
      }
      continue;
    }

    nestedDefs.push({
      groupId: getMetricGroupId(parentColumn.id),
      headerName: parentColumn.title,
      marryChildren: true,
      openByDefault: false,
      children: withPinGroupContext(
        [{ ...columnDef, headerName: resolveRootLeafHeader(parentColumn) }, ...groupedChildren],
        parentColumn.title,
      ),
    } as SmartTableColumnDef<TData>);
  }

  return nestedDefs.filter(columnDef => {
    if (isSmartTableColumnGroupDef(columnDef)) {
      return true;
    }
    const leafColumnId = getLeafColumnId(columnDef);
    return !leafColumnId || !consumedChildIds.has(leafColumnId);
  });
};
